import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { paypalSandboxAPI } from '../lib/api';
import { toast } from 'sonner';
import { FlaskConical, ShieldCheck, ShieldAlert, RefreshCw, Mail, CheckCircle2, XCircle } from 'lucide-react';

const Flag = ({ ok, label, testId }) => (
  <div className="flex items-center gap-2 text-sm" data-testid={testId}>
    {ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-slate-400" />}
    <span className={ok ? 'text-slate-800' : 'text-slate-500'}>{label}</span>
  </div>
);

const errMsg = (e) => e?.response?.data?.detail || e?.message || 'Erreur inconnue';

export default function AdminPaypalSandboxPage() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({ client_id: '', secret: '', buyer_email: '', buyer_password: '' });
  const [testTo, setTestTo] = useState('');
  const [emailResult, setEmailResult] = useState(null);
  const [live, setLive] = useState(null);

  const load = async (verify = false) => {
    setBusy('status');
    try { setSt((await paypalSandboxAPI.status(verify)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };
  useEffect(() => { load(); }, []);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); setSt(r.data); toast.success(okMsg); return r.data; } catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };

  const save = (e) => {
    e.preventDefault();
    const payload = { client_id: form.client_id, secret: form.secret };
    if (form.buyer_email) payload.buyer_email = form.buyer_email;
    if (form.buyer_password) payload.buyer_password = form.buyer_password;
    run('save', () => paypalSandboxAPI.saveConfig(payload), 'Identifiants Sandbox validés auprès de PayPal et enregistrés').then((d) => {
      if (d) setForm({ client_id: '', secret: '', buyer_email: '', buyer_password: '' });
    });
  };

  const sendTest = async () => {
    setBusy('email'); setEmailResult(null);
    try { const r = await paypalSandboxAPI.testEmail(testTo); setEmailResult(r.data); r.data.sent ? toast.success('Email de test envoyé') : toast.error('Brevo a refusé l\'envoi'); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };

  const liveCheck = async () => {
    setBusy('live');
    try { const r = await paypalSandboxAPI.liveCheck(); setLive(r.data); r.data.all_ok ? toast.success('Configuration PayPal effective valide') : toast.error('Configuration PayPal effective incomplète'); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(''); }
  };

  const sandboxOn = st?.enabled;

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4 md:p-6" data-testid="admin-paypal-sandbox-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2"><FlaskConical className="h-6 w-6 text-amber-600" />PayPal Sandbox — environnement de test</h1>
          <p className="text-sm text-slate-500 mt-1">Isolé des identifiants LIVE (jamais modifiés ici). Aucun paiement réel.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={!!busy} data-testid="sandbox-refresh-btn"><RefreshCw className={`h-4 w-4 mr-2 ${busy === 'status' ? 'animate-spin' : ''}`} />Vérifier</Button>
      </div>

      <Card className={sandboxOn ? 'border-amber-400 bg-amber-50/60' : 'border-slate-200'} data-testid="sandbox-mode-card">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {sandboxOn ? <ShieldAlert className="h-8 w-8 text-amber-600" /> : <ShieldCheck className="h-8 w-8 text-emerald-600" />}
            <div>
              <div className="font-semibold text-slate-900" data-testid="sandbox-mode-label">Mode effectif de cet environnement : <span className="uppercase">{st?.mode_effectif || '…'}</span></div>
              <p className="text-xs text-slate-500">{sandboxOn ? 'Les paiements de /achat utilisent les plans Sandbox (faux argent).' : 'Les paiements de /achat utilisent les plans LIVE.'}</p>
            </div>
          </div>
          <Button
            variant={sandboxOn ? 'destructive' : 'default'}
            disabled={!!busy || !st || (!sandboxOn && !(st.monthly_plan_id && st.annual_plan_id))}
            onClick={() => run('toggle', () => paypalSandboxAPI.toggle(!sandboxOn), sandboxOn ? 'Retour au mode LIVE' : 'Mode SANDBOX activé')}
            data-testid="sandbox-toggle-btn"
          >
            {sandboxOn ? 'Désactiver le Sandbox (retour LIVE)' : 'Activer le mode Sandbox'}
          </Button>
        </CardContent>
      </Card>

      <Card data-testid="live-check-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0"><CardTitle className="text-base">0. Configuration PayPal effective de cet environnement (lecture seule)</CardTitle>
          <Button variant="outline" size="sm" onClick={liveCheck} disabled={!!busy} data-testid="live-check-btn">{busy === 'live' ? 'Vérification…' : 'Vérifier identifiants + plans'}</Button></CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p className="text-xs text-slate-500">Teste l'authentification auprès de PayPal et l'accès aux deux plans avec l'application configurée. Aucun secret affiché, aucun paiement.</p>
          {live && (
            <div className="space-y-1" data-testid="live-check-result">
              <Flag ok={live.oauth_ok} label={`Application ${live.mode.toUpperCase()} : Client ID ${live.client_id_prefix}… (${live.client_id_length} car.), secret ${live.secret_present ? 'présent' : 'ABSENT'} — authentification PayPal ${live.oauth_ok ? 'OK' : `KO (${live.oauth_error || ''})`}`} testId="live-flag-oauth" />
              {live.plans.map((p) => <Flag key={p.plan} ok={p.ok} label={`Plan ${p.plan} ${p.plan_id} : ${p.ok ? `OK — ${p.price_eur} € / ${p.unit}, ${p.status}, cycles illimités` : `KO — ${p.detail || `${p.status} ${p.unit} ${p.price_eur}`}`}`} testId={`live-flag-plan-${p.plan}`} />)}
              <Flag ok={live.all_ok} label={live.all_ok ? 'Configuration cohérente : identifiants valides et plans exploitables' : 'Configuration à corriger avant tout test réel'} testId="live-flag-all" />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">1. Identifiants Sandbox</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">developer.paypal.com → Apps &amp; Credentials → onglet <strong>Sandbox</strong> → Create App → Client ID + Secret. Transmis en HTTPS au serveur de test uniquement, stockés hors git, jamais affichés.</p>
            <form onSubmit={save} className="space-y-3">
              <div><Label htmlFor="sb-cid">Client ID Sandbox</Label><Input id="sb-cid" autoComplete="off" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="sandbox-client-id-input" /></div>
              <div><Label htmlFor="sb-sec">Secret Sandbox</Label><Input id="sb-sec" type="password" autoComplete="new-password" value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} data-testid="sandbox-secret-input" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label htmlFor="sb-be">Acheteur sandbox (email)</Label><Input id="sb-be" autoComplete="off" placeholder="optionnel" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} data-testid="sandbox-buyer-email-input" /></div>
                <div><Label htmlFor="sb-bp">Acheteur (mot de passe)</Label><Input id="sb-bp" type="password" autoComplete="new-password" placeholder="optionnel" value={form.buyer_password} onChange={(e) => setForm({ ...form, buyer_password: e.target.value })} data-testid="sandbox-buyer-password-input" /></div>
              </div>
              <Button type="submit" className="w-full" disabled={!!busy || !form.client_id || !form.secret} data-testid="sandbox-save-btn">{busy === 'save' ? 'Validation auprès de PayPal…' : 'Valider et enregistrer'}</Button>
            </form>
            <div className="pt-2 space-y-1">
              <Flag ok={st?.configured} label={`Identifiants enregistrés ${st?.client_id_masque ? `(Client ID ${st.client_id_masque})` : ''}`} testId="sandbox-flag-configured" />
              <Flag ok={!!st?.buyer_email} label={st?.buyer_email ? `Compte acheteur : ${st.buyer_email}` : 'Compte acheteur sandbox non renseigné'} testId="sandbox-flag-buyer" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">2. Plans d'abonnement Sandbox</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">Crée le produit et les deux plans de test (29,99 €/mois · 299 €/an, cycles illimités) dans votre compte Sandbox. Idempotent.</p>
            <Button className="w-full" variant="outline" disabled={!!busy || !st?.configured} onClick={() => run('plans', paypalSandboxAPI.createPlans, 'Plans Sandbox prêts')} data-testid="sandbox-create-plans-btn">{busy === 'plans' ? 'Création…' : 'Créer / vérifier les plans Sandbox'}</Button>
            <div className="space-y-1 text-sm">
              <Flag ok={!!st?.monthly_plan_id} label={`Mensuel : ${st?.monthly_plan_id || 'non créé'}`} testId="sandbox-flag-monthly" />
              <Flag ok={!!st?.annual_plan_id} label={`Annuel : ${st?.annual_plan_id || 'non créé'}`} testId="sandbox-flag-annual" />
            </div>
            {st?.plans_verification && (
              <div className="rounded-md bg-slate-50 border p-3 text-xs space-y-1" data-testid="sandbox-plans-verification">
                {st.plans_verification.map((p) => (
                  <div key={p.plan} className={p.ok ? 'text-emerald-700' : 'text-red-700'}>
                    {p.plan} : {p.ok ? `OK — ${p.price_eur} € / ${p.unit}, ${p.status}, cycles illimités` : `KO — ${p.detail || `${p.status} ${p.unit} ${p.price_eur}`}`}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">3. Garde-fous &amp; notifications</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-3 text-sm">
          <Flag ok={st?.live_intact} label="Identifiants LIVE intacts (PAYPAL_MODE=live, clés présentes)" testId="sandbox-flag-live-intact" />
          <Flag ok={st?.live_plan_ids_intacts} label="Plans LIVE intacts (mensuel + annuel)" testId="sandbox-flag-live-plans" />
          <Flag ok={!st?.host_is_prod} label={`Domaine courant : ${st?.host || '…'} ${st?.host_is_prod ? '(PRODUCTION — sandbox interdit)' : '(environnement de test)'}`} testId="sandbox-flag-host" />
          <Flag ok={true} label="Sur app.capsulepatrimoine.com, /achat refuse de servir un bouton Sandbox (503 + message)" testId="sandbox-flag-guard" />
          <div className="md:col-span-2 rounded-md bg-slate-50 border p-3">
            <div className="text-xs text-slate-500 mb-1">URL IPN à renseigner dans le compte Business <strong>sandbox</strong> (sandbox.paypal.com → Account settings → Notifications → Instant payment notifications) :</div>
            <code className="text-xs break-all" data-testid="sandbox-ipn-url">{st?.ipn_url || '…'}</code>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" />4. Test d'envoi réel Brevo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">Envoie un vrai email de test via Brevo vers l'adresse de votre choix (aucun identifiant n'est journalisé).</p>
          <div className="flex gap-2">
            <Input type="email" placeholder="votre@adresse.fr" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-email-input" />
            <Button onClick={sendTest} disabled={!!busy || !testTo} data-testid="test-email-btn">{busy === 'email' ? 'Envoi…' : 'Envoyer'}</Button>
          </div>
          {emailResult && <Flag ok={emailResult.sent} label={emailResult.sent ? `Envoyé à ${emailResult.to} depuis ${emailResult.sender}` : 'Échec — voir journaux serveur (réponse Brevo)'} testId="test-email-result" />}
        </CardContent>
      </Card>
    </div>
  );
}
